import React, { useState } from "react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
// import { useApp } from "contexts/BookingContext";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import { AgentProvider } from "contexts/v1/masterData/AgentContext";
import Swal from "sweetalert2";
import CreateKomisi from "./components/create-komisi";
import DateInputWithIcon from "./components/DateInputWithIcon";
import {
  IconChevronLeft,
  IconChevronRight,
  IconFilter,
  IconFilterOff,
} from "@tabler/icons-react";
import LogReservation from "./components/log-reservation";
import { LogReservationProvider } from "contexts/LogReservationContext";
import PrintBookingDetails from "./components/PrintBookingDetails";
import { useAppV3 } from "contexts/v3/BookingContextV3";
import ExportExcel from "./components/ExportExcel";
import { formatPrice } from "utils/utils";
import { Badge, Table } from "react-bootstrap";
import BookingDetailsComponent from "./booking-details";

const BookingReport = () => {
  const { showCanvasHandler } = useCanvasHandler();
  const {
    sessions,
    bookings,
    filterOptions,
    updateFilter,
    refetch,
    handleSearch,
  } = useAppV3();
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true); // State untuk show/hide filter

  // 🔹 Date Navigation Handler
  const changeDate = (days) => {
    const currentDate = new Date(
      filterOptions.filters.reservation_date || new Date()
    );
    currentDate.setDate(currentDate.getDate() + days);
    updateFilter("reservation_date", currentDate.toISOString().split("T")[0]);
  };

  // 🔹 Show Komisi Form
  const handleUpdate = (e, booking) => {
    e.preventDefault();
    showCanvasHandler(
      "Komisi Agent",
      <AgentProvider>
        <CreateKomisi booking={booking} />
      </AgentProvider>
    );
  };

  const handleLog = (e, booking) => {
    e.preventDefault();
    // console.log("handleLog booking", booking);

    showCanvasHandler(
      "Log",
      <BookingDetails booking={booking} />,
      "fullscreen-canvas"
    );
  };

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-5">
        {/* 🔹 Date Filter */}
        <div className="row mb-3">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <h1>Reservasi Report</h1>
            <button
              className="btn btn-outline-primary"
              onClick={() => setShowFilters(!showFilters)}
            >
              {/* {showFilters ? "Hide Filters" : "Show Filters"} */}
              {showFilters ? (
                <IconFilterOff size={24} />
              ) : (
                <IconFilter size={24} />
              )}
            </button>
          </div>
        </div>
        {showFilters && (
          <div className="row mb-3 justify-content-between align-items-center g-3">
            <div className="col-md-4 col-sm-6">
              <SearchInput
                filterOptions={filterOptions}
                handleSearch={handleSearch}
              />
            </div>

            <div className="col-md-4 col-sm-6">
              <ExportExcel
                reservationDate={filterOptions.filters.reservation_date}
              />
            </div>
            <div className="col-md-4 col-sm-12">
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn"
                  onClick={() => changeDate(-1)}
                  title="Previous Day"
                >
                  <IconChevronLeft size={18} />
                </button>
                <DateInputWithIcon
                  value={filterOptions.filters.reservation_date}
                  onChange={(date) => updateFilter("reservation_date", date)}
                />
                <button
                  className="btn"
                  onClick={() => changeDate(1)}
                  title="Next Day"
                >
                  <IconChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🔹 Booking Table */}
        <div className="table-responsive">
          <table className="table table-bordered table-hover">
            <thead>
              <tr>
                <th>#</th>
              </tr>
            </thead>
            <tbody>
              {sessions?.map((session) => (
                <SessionBlock
                  key={session.id}
                  session={session}
                  handleUpdate={handleUpdate}
                  handleLog={handleLog}
                  refetch={refetch}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </LayoutDashboard>
  );
};

// 🔹 SessionBlock Component
const SessionBlock = ({ session, handleUpdate, handleLog, refetch }) => {
  const { dataPerTrip } = useAppV3();
  const bookingsForSession = dataPerTrip[session.id] || [];

  return (
    <div className="col-12 mb-3">
      <div className="shadow-sm card-metric card p-0 bg-light">
        <div className="card-header">
          <h2>{session.title}</h2>
        </div>
        <div className="card-body ">
          <BookingTable
            bookings={bookingsForSession}
            handleUpdate={handleUpdate}
            handleLog={handleLog}
            refetch={refetch}
          />
        </div>
      </div>
    </div>
  );
};

// 🔹 BookingTable Component
const BookingTable = ({ bookings, handleLog, refetch }) => {
  // console.log("BookingTable", bookings);
  const getTextColor = (backgroundColor) => {
    const hex = backgroundColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 128 ? "#fff" : "#000";
  };
  return (
    <div className="table-responsive">
      <table className="table table-hover table-vcenter table-sm">
        <thead>
          <tr>
            <th>Code #</th>
            <th>Guest</th>
            <th>Driver</th>
            <th>Hotel</th>
            <th>Agent</th>
            <th>Label</th>
            <th>Activity</th>
            <th>PAX</th>
            <th>Total</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {bookings?.map((booking, index) => (
            <tr key={index}>
              <td>{booking.code}</td>
              <td>{booking.guest_name}</td>
              {/* <td><GetAgentName agentId={booking.agent_id} /></td> */}
              <td>{booking.driver || "-"}</td>
              <td>{booking.customer?.nationality || "-"}</td>
              <td>
                <span
                  className="badge text-uppercase"
                  style={{
                    backgroundColor: booking?.agent?.color || "#6c757d",
                    color: getTextColor(booking?.agent?.color || "#6c757d"),
                  }}
                >
                  {booking.agent?.name}
                </span>
              </td>
              <td>
                  {booking.labels?.length > 0 ? (
                    booking.labels.map((label, index) => (
                      <span
                        key={index}
                        className="badge text-uppercase me-1"
                        style={{
                          backgroundColor: label.color || "#6c757d",
                          color: getTextColor(label.color || "#6c757d"), // Warna teks dinamis
                        }}
                      >
                        {label.name || "No Label"}
                      </span>
                    ))
                  ) : (
                    <span className="badge text-uppercase">No Label</span>
                  )}
                </td>
              <td>
                {booking.activities.map((a, i) => (
                  <div key={i}>
                    {/* {a.activity} ({a.qty}) */}({a.qty}x) : {a.activity} : {formatPrice(a.price)}
                  </div>
                ))}
              </td>
              <td>{booking.guest_pax}</td>
              <td>Rp. {parseInt(booking.total_price).toLocaleString()}</td>
              <td>
                <div className="d-flex gap-2">
                  <PrintBookingDetails
                    onSuccess={() => {
                      Swal.fire("Print Success", "", "success");
                      refetch();
                    }}
                    booking={booking}
                  />
                  <button
                    className="btn position-relative btn-sm"
                    onClick={(e) => handleLog(e, booking)}
                  >
                    LOG
                    <span className="badge bg-pink text-pink-fg badge-notification badge-pill">
                      {booking.logs_count}
                      {/* {Math.max(0, booking.logs_count - 1)} */}
                    </span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// 🔹 GetAgentName Component
// const GetAgentName = ({ agentId }) => {
//   const { agents } = useAgent();
//   const agent = agents.data.find((a) => a.id === agentId);

//   return (
//     <span className="badge text-uppercase" style={{ backgroundColor: agent?.color || "#6c757d" }}>
//       {agent?.name || "Unknown"}
//     </span>
//   );
// };

export default BookingReport;

const BookingDetails = ({ booking, logs }) => {
  return (
    <div className="d-flex flex-column gap-4">
      {/* Card for Booking Details */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Booking Details</h3>
        </div>
        {/* <div className="card-body">
          <div className="d-flex flex-column gap-3">
            <div className="d-flex gap-2">
              <div className="fw-bold">Agent:</div>
              <div>{booking.agent_id}</div>
            </div>
            <div className="d-flex gap-2">
              <div className="fw-bold">Booking ID:</div>
              <div>{booking.id}</div>
            </div>
            <div className="d-flex gap-2">
              <div className="fw-bold">Guest Name:</div>
              <div>{booking.guest_name}</div>
            </div>
            <div className="d-flex gap-2">
              <div className="fw-bold">Guest Pax:</div>
              <div>{booking.guest_pax}</div>
            </div>
            <div className="d-flex gap-2">
              <div className="fw-bold">Total Price:</div>
              <div>{formatPrice(booking.total_price)}</div>
            </div>
            <div className="d-flex gap-2">
              <div className="fw-bold">Payment Note:</div>
              <div>{booking.payment_note}</div>
            </div>
            <div className="d-flex gap-2">
              <div className="fw-bold">Activities:</div>
              <div>
                {booking.activities.map((activity, index) => (
                  <div key={index}>
                    {activity.activity} ({activity.qty})
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div> */}
        <BookingDetailsComponent booking={booking} />
      </div>

      {/* Card for Log History */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Log History</h3>
        </div>
        <LogReservationProvider>
          <LogReservation booking={booking} />
        </LogReservationProvider>
      </div>
    </div>
  );
};

// Search Input Component
const SearchInput = ({ handleSearch, filterOptions }) => {
  // const { handleSearch, filterOptions } = useApp();
  const [search, setSearch] = useState(filterOptions.search || "");

  return (
    <div className="input-icon">
      <input
        type="text"
        className="form-control"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleSearch}
      />
      <span className="input-icon-addon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-tabler icon-tabler-search"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <circle cx="10" cy="10" r="7" />
          <line x1="21" y1="21" x2="15" y2="15" />
        </svg>
      </span>
    </div>
  );
};
